import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

// 2026-08-25 — 백엔드 Notification.level이 자유 문자열에서 정식 Prisma enum으로 전환되며
// 값이 소문자('info' 등)에서 스키마 멤버 이름 그대로(PascalCase, 'Info' 등)로 바뀌었다 —
// Prisma의 enum @map은 DB 저장값만 바꾸고 Client가 노출하는 런타임 값은 항상 스키마 멤버
// 이름이다(OrgAuditAction 등 이 워크스페이스의 다른 모든 Prisma enum과 동일 동작). "클라이언트
// lib은 백엔드 Nest lib에 의존하지 않는다"는 이 워크스페이스 원칙(spinoff-auth.service.ts
// 참고, dependency-error-code.enum.ts/credit-error-code.enum.ts와 동일 패턴)에 따라 여기서도
// import 대신 값을 손으로 복제해 둔다 — 각 앱의 orval `NotificationListItemDtoLevel`과 값이
// 동일해야 한다(둘 다 같은 백엔드 Prisma enum에서 파생, 컨벤션으로 동기화 유지).
export const NotificationLevel = {
  Info: 'Info',
  Success: 'Success',
  Warning: 'Warning',
  Error: 'Error',
} as const;
export type NotificationLevel = (typeof NotificationLevel)[keyof typeof NotificationLevel];

/**
 * `@sbe-spinoff-backend/spinoff-platform`의 `SpinoffNotificationsController`가 내려주는
 * 통합 인박스 알림 한 건 — 어느 앱이든 이 컨트롤러 모듈을 마운트하면 필드가 동일하다.
 */
export interface NotificationListItem {
  id: string;
  orgId?: string | null;
  category: string;
  level: NotificationLevel;
  title: string;
  body?: string | null;
  linkPath?: string | null;
  readAt?: string | null;
  createdAt: string;
}

/**
 * 알림 API 호출 계약 — `SpinoffNotificationBellComponent`가 이 토큰으로 주입받는다.
 *
 * 알림 엔드포인트(`GET /notifications` 등)는 spinoff-platform이 아니라 **각 소비 앱 자신의
 * 백엔드**에 `SpinoffNotificationsControllerModule`로 마운트된다(org-switcher가 쓰는
 * `SpinoffOrgsStore`처럼 중앙 spinoff-platform 서비스를 직접 호출하는 게 아니다) — 그래서
 * 이 lib이 특정 orval 생성 client를 직접 baked-in할 수 없다. 각 앱이 자기 앱의 생성
 * client(`SBETimetableAPIService`, `SbeSmsApiService` 등)를 감싸는 로컬
 * `NotificationsApiService`를 만들고, 이 토큰에 `useExisting`으로 연결한다.
 */
export interface NotificationsApiClient {
  list(params: { page?: number; size?: number }): Observable<{ items: NotificationListItem[]; total: number }>;
  unreadCount(): Observable<{ count: number }>;
  markRead(id: string): Observable<void>;
  markAllRead(): Observable<{ updated: number }>;
}

export const NOTIFICATIONS_API_CLIENT = new InjectionToken<NotificationsApiClient>('NOTIFICATIONS_API_CLIENT');
